import type { CustomerProduct } from '@restaurant/api-contracts';
import { create } from 'zustand';
import { type DraftChoice, type DraftLine, lineKey } from '../lib/roundDraft';

/**
 * The in-progress round being built on the Add-a-round page. Lives in a store
 * (not page state) because the flow spans two pushed pages - the menu page and
 * the product-configure page - so the configure page can commit a line and
 * `router.back()` without prop-drilling callbacks through the F7 router. Scoped
 * to one session at a time; `reset(sessionId)` clears it when a different tab
 * starts a round.
 */
interface RoundDraftState {
  sessionId: string | null;
  lines: DraftLine[];
  note: string;
  reset: (sessionId: string) => void;
  addLine: (product: CustomerProduct, choices: DraftChoice[], editKey: string | null) => void;
  setQty: (key: string, qty: number) => void;
  setSeat: (key: string, seatId: string | null) => void;
  setNote: (note: string) => void;
  clear: () => void;
}

export const useRoundDraft = create<RoundDraftState>((set) => ({
  sessionId: null,
  lines: [],
  note: '',
  reset: (sessionId) => set({ sessionId, lines: [], note: '' }),
  addLine: (product, choices, editKey) =>
    set((state) => {
      // New lines start Shared; an edit carries the line's existing seat.
      const prior = editKey ? state.lines.find((l) => l.key === editKey) : undefined;
      const seatId = prior?.seatId ?? null;
      const key = lineKey(product.id, choices, seatId);
      const carryQty = prior?.quantity ?? 1;
      const base = editKey ? state.lines.filter((l) => l.key !== editKey) : state.lines;
      const existing = base.find((l) => l.key === key);
      const lines = existing
        ? base.map((l) => (l.key === key ? { ...l, quantity: l.quantity + carryQty } : l))
        : [...base, { key, product, quantity: carryQty, choices, seatId }];
      return { lines };
    }),
  setQty: (key, qty) =>
    set((state) => ({
      lines:
        qty <= 0
          ? state.lines.filter((l) => l.key !== key)
          : state.lines.map((l) => (l.key === key ? { ...l, quantity: qty } : l)),
    })),
  setSeat: (key, seatId) =>
    set((state) => {
      const line = state.lines.find((l) => l.key === key);
      if (!line || line.seatId === seatId) return state;
      const newKey = lineKey(line.product.id, line.choices, seatId);
      const rest = state.lines.filter((l) => l.key !== key);
      // Same dish already tagged to that seat → merge quantities into it.
      const twin = rest.find((l) => l.key === newKey);
      const lines = twin
        ? rest.map((l) => (l.key === newKey ? { ...l, quantity: l.quantity + line.quantity } : l))
        : [...rest, { ...line, key: newKey, seatId }];
      return { lines };
    }),
  setNote: (note) => set({ note }),
  clear: () => set({ sessionId: null, lines: [], note: '' }),
}));
